import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import Icon from '../../../components/AppIcon';
import { bankingService } from '../../../services/bankingService';
import { chartOfAccountsService } from '../../../services/chartOfAccountsService';

const TransactionCategoryManager = ({ isOpen, onClose, organizationId }) => {
  const [categories, setCategories] = useState([]);
  const [categoryMappings, setCategoryMappings] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('mappings'); // mappings, rules, analytics

  // Load data on component mount
  useEffect(() => {
    if (isOpen && organizationId) {
      loadCategoryData();
    }
  }, [isOpen, organizationId]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load predefined categories, mappings, and chart of accounts
      const [categoriesData, mappingsData, accountsData] = await Promise.all([
        bankingService?.getTransactionCategories(),
        bankingService?.getCategoryMappings(organizationId),
        chartOfAccountsService?.getChartOfAccounts()
      ]);

      // Ensure all data is arrays to prevent find() errors
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setCategoryMappings(Array.isArray(mappingsData) ? mappingsData : []);
      setChartAccounts(Array.isArray(accountsData) ? accountsData : []);

    } catch (err) {
      console.error('Error loading category data:', err);
      setError(err?.message || 'Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMapping = async (categoryKey, accountId) => {
    try {
      await bankingService?.updateCategoryMapping(organizationId, categoryKey, accountId);
      await loadCategoryData(); // Refresh data
      console.log('Category mapping updated successfully');
    } catch (err) {
      console.error('Error updating category mapping:', err);
      setError(err?.message || 'Failed to update category mapping');
    }
  };

  const handleCreateMapping = async (categoryKey, accountId, description) => {
    try {
      await bankingService?.createCategoryMapping({
        organizationId,
        categoryName: categoryKey,
        accountId,
        description,
        isActive: true
      });
      await loadCategoryData(); // Refresh data
      console.log('Category mapping created successfully');
    } catch (err) {
      console.error('Error creating category mapping:', err);
      setError(err?.message || 'Failed to create category mapping');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Are you sure you want to delete this category mapping?')) {
      return;
    }

    try {
      await bankingService?.deleteCategoryMapping(mappingId);
      await loadCategoryData(); // Refresh data
      console.log('Category mapping deleted successfully');
    } catch (err) {
      console.error('Error deleting category mapping:', err);
      setError(err?.message || 'Failed to delete category mapping');
    }
  };

  const formatAccountOptions = () => {
    // Add array validation to prevent mapping errors
    if (!Array.isArray(chartAccounts)) {
      return [];
    }
    
    return chartAccounts?.map(account => ({
      value: account?.id,
      label: `${account?.accountCode} - ${account?.accountName}`,
      group: account?.accountType
    })) || [];
  };

  const getCategoryIcon = (iconName) => {
    return iconName || 'Tag';
  };

  const getCategoryColor = (colorClass) => {
    return colorClass || 'text-gray-600';
  };

  const renderMappingsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Category Mappings</h3>
          <p className="text-sm text-muted-foreground">
            Map transaction categories to chart of accounts for proper accounting classification
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => bankingService?.createDefaultCategoryMappings(organizationId)}
          iconName="RefreshCw"
          iconPosition="left"
        >
          Auto-Create Mappings
        </Button>
      </div>

      {/* Category Mappings Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-foreground">Category</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Mapped Account</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(categories) && categories?.map(category => {
              // Add array validation before using find methods
              const mapping = Array.isArray(categoryMappings) 
                ? categoryMappings?.find(m => m?.categoryName === category?.categoryKey)
                : null;
              const mappedAccount = Array.isArray(chartAccounts) 
                ? chartAccounts?.find(a => a?.id === mapping?.accountId)
                : null;
              
              return (
                <tr key={category?.id} className="border-b border-border hover:bg-muted/20">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${category?.colorClass || 'text-gray-600'} bg-muted/20`}>
                        <Icon name={getCategoryIcon(category?.iconName)} size={16} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{category?.categoryName}</p>
                        <p className="text-xs text-muted-foreground">{category?.categoryGroup}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {mapping ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {mappedAccount?.accountCode} - {mappedAccount?.accountName}
                          </p>
                          <p className="text-xs text-muted-foreground">{mappedAccount?.accountType}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMapping(mapping?.id)}
                          iconName="X"
                          className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                          title="Remove mapping"
                        />
                      </div>
                    ) : (
                      <Select
                        options={formatAccountOptions()}
                        placeholder="Select account..."
                        value=""
                        onChange={(accountId) => handleCreateMapping(category?.categoryKey, accountId, `Mapping for ${category?.categoryName}`)}
                        className="w-64"
                        groupBy="group"
                      />
                    )}
                  </td>
                  <td className="p-4">
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                      mapping?.isActive ? 'text-success bg-success/10' : 'text-muted-foreground bg-muted/10'
                    }`}>
                      <Icon name={mapping?.isActive ? 'CheckCircle' : 'Circle'} size={12} />
                      <span>{mapping?.isActive ? 'Active' : 'Not Mapped'}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      {mapping && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newAccountId = prompt('Select new account ID:');
                            if (newAccountId) {
                              handleUpdateMapping(category?.categoryKey, newAccountId);
                            }
                          }}
                          iconName="Edit"
                          title="Edit mapping"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Categorization Analytics</h3>
        <p className="text-sm text-muted-foreground">
          Insights into transaction categorization and AI accuracy
        </p>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted-foreground">Categorization Rate</h4>
            <Icon name="PieChart" size={16} className="text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">85%</p>
          <p className="text-sm text-muted-foreground mt-1">Of transactions categorized</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted-foreground">AI Accuracy</h4>
            <Icon name="Brain" size={16} className="text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">92%</p>
          <p className="text-sm text-muted-foreground mt-1">AI suggestions accepted</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-muted-foreground">Top Category</h4>
            <Icon name="TrendingUp" size={16} className="text-green-600" />
          </div>
          <p className="text-lg font-bold text-foreground">Office Supplies</p>
          <p className="text-sm text-muted-foreground mt-1">42% of expenses</p>
        </div>
      </div>

      {/* Category Usage Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">Category Usage</h4>
        <div className="space-y-3">
          {Array.isArray(categories) && categories?.slice(0, 6)?.map((category, index) => {
            const percentage = Math.random() * 100; // Mock data
            return (
              <div key={category?.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getCategoryIcon(category?.iconName)} 
                    size={16} 
                    className={getCategoryColor(category?.colorClass)} 
                  />
                  <span className="text-sm text-foreground">{category?.categoryName}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-20 bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {percentage?.toFixed(0)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Category Management</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage transaction categories and account mappings for better financial organization
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-error/10 border-b border-error/20 p-4">
            <div className="flex items-center space-x-3">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{error}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError('')}
                iconName="X"
                className="ml-auto"
              />
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center border-b border-border">
          <Button
            variant={activeTab === 'mappings' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('mappings')}
            className="rounded-none border-b-2 border-transparent data-[active=true]:border-primary"
          >
            Account Mappings
          </Button>
          <Button
            variant={activeTab === 'analytics' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('analytics')}
            className="rounded-none border-b-2 border-transparent data-[active=true]:border-primary"
          >
            Analytics
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading category data...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'mappings' && renderMappingsTab()}
              {activeTab === 'analytics' && renderAnalyticsTab()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Category mappings help automatically post transactions to the correct accounting accounts</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionCategoryManager;